/**
 * Exit Popup Custom Component
 * Handles exit intent detection, mobile back button, swipe gestures, and form submission
 */
class ExitPopup extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.hasShown = false;
    this.historyStatePushed = false;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.swipeThreshold = 50;
    this.exitIntentTriggered = false;
    
    // Get settings from data attributes
    this.settings = {
      exitIntent: this.dataset.triggerExitIntent === 'true',
      pageUnload: this.dataset.triggerPageUnload === 'true',
      mobileBack: this.dataset.triggerMobileBack === 'true',
      mobileSwipe: this.dataset.triggerMobileSwipe === 'true',
      manual: this.dataset.triggerManual === 'true',
      delay: parseFloat(this.dataset.delay) || 0,
      showOnceSession: this.dataset.showOnceSession === 'true',
      showOnceDay: this.dataset.showOnceDay === 'true'
    };

    // Get DOM elements
    this.container = this.querySelector('.exit-popup-container');
    this.overlay = this.querySelector('.exit-popup-overlay');
    this.form = this.querySelector('.exit-popup-form');
    this.closeButtons = this.querySelectorAll('[data-close-popup]');
    this.submitButton = this.querySelector('[data-submit-button]');
    this.successMessage = this.querySelector('.exit-popup-success');
    
    // Get swipe threshold from settings if available
    const swipeThresholdEl = document.querySelector('[data-swipe-threshold]');
    if (swipeThresholdEl) {
      this.swipeThreshold = parseInt(swipeThresholdEl.dataset.swipeThreshold) || 50;
    }
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    // Check if already shown (session/day restrictions)
    if (this.shouldSkip()) {
      return;
    }

    // Setup event listeners
    this.setupEventListeners();
    
    // Setup mobile back button detection
    if (this.settings.mobileBack) {
      this.setupMobileBackDetection();
    }

    // Setup mobile swipe detection
    if (this.settings.mobileSwipe) {
      this.setupMobileSwipeDetection();
    }

    // Setup exit intent detection (desktop)
    if (this.settings.exitIntent) {
      this.setupExitIntentDetection();
    }

    // Setup page unload detection
    if (this.settings.pageUnload) {
      this.setupPageUnloadDetection();
    }

    // Setup manual trigger
    if (this.settings.manual) {
      this.setupManualTrigger();
    }
  }

  setupEventListeners() {
    // Close buttons
    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.hide());
    });

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.hide();
      }
    });

    // Click overlay to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.hide());
    }

    // Prevent closing when clicking inside popup
    if (this.container) {
      this.container.addEventListener('click', (e) => e.stopPropagation());
    }

    // Focus trap
    this.setupFocusTrap();
  }

  setupFocusTrap() {
    if (!this.container) return;

    this.container.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || !this.isOpen) return;

      const focusableElements = this.container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    });
  }

  setupExitIntentDetection() {
    // Only for desktop (non-touch devices)
    if ('ontouchstart' in window) return;

    let mouseY = 0;
    
    document.addEventListener('mousemove', (e) => {
      mouseY = e.clientY;
    });

    document.addEventListener('mouseleave', (e) => {
      // Check if mouse is leaving toward the top
      if (e.clientY <= 0 && mouseY <= 5 && !this.exitIntentTriggered) {
        this.exitIntentTriggered = true;
        this.show();
      }
    });
  }

  setupMobileBackDetection() {
    // Only for mobile/touch devices
    if (!('ontouchstart' in window)) return;

    // Push a state to history
    if (!this.historyStatePushed) {
      window.history.pushState({ exitPopup: true }, '');
      this.historyStatePushed = true;
    }

    // Listen for popstate (back button)
    window.addEventListener('popstate', (e) => {
      if (e.state && e.state.exitPopup) {
        // Prevent navigation
        e.preventDefault();
        // Push state again
        window.history.pushState({ exitPopup: true }, '');
        // Show popup
        this.show();
      }
    });
  }

  setupMobileSwipeDetection() {
    // Only for mobile/touch devices
    if (!('ontouchstart' in window)) return;

    document.addEventListener('touchstart', (e) => {
      const touch = e.touches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
    }, { passive: true });

    document.addEventListener('touchmove', (e) => {
      if (!this.touchStartX || !this.touchStartY) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;

      // Check if swipe starts from left edge (< 20px from left)
      if (this.touchStartX < 20 && deltaX > this.swipeThreshold && Math.abs(deltaY) < Math.abs(deltaX)) {
        // Swipe right from left edge detected
        this.show();
        this.touchStartX = 0;
        this.touchStartY = 0;
      }
    }, { passive: true });

    document.addEventListener('touchend', () => {
      this.touchStartX = 0;
      this.touchStartY = 0;
    }, { passive: true });
  }

  setupPageUnloadDetection() {
    window.addEventListener('beforeunload', (e) => {
      if (!this.hasShown) {
        // Note: Modern browsers limit what we can do here
        // We can't show a custom popup, but we can trigger our popup before unload
        this.show();
      }
    });
  }

  setupManualTrigger() {
    // Listen for manual triggers via data attribute
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-trigger-popup]');
      if (trigger && trigger.dataset.triggerPopup === this.id) {
        e.preventDefault();
        this.show();
      }
    });
  }

  shouldSkip() {
    // Check session storage
    if (this.settings.showOnceSession) {
      const sessionKey = `exit-popup-shown-${this.id}`;
      if (sessionStorage.getItem(sessionKey)) {
        return true;
      }
    }

    // Check localStorage (once per day)
    if (this.settings.showOnceDay) {
      const dayKey = `exit-popup-shown-${this.id}`;
      const lastShown = localStorage.getItem(dayKey);
      if (lastShown) {
        const lastShownDate = new Date(lastShown);
        const today = new Date();
        if (lastShownDate.toDateString() === today.toDateString()) {
          return true;
        }
      }
    }

    return false;
  }

  markAsShown() {
    // Mark in session storage
    if (this.settings.showOnceSession) {
      const sessionKey = `exit-popup-shown-${this.id}`;
      sessionStorage.setItem(sessionKey, 'true');
    }

    // Mark in localStorage (once per day)
    if (this.settings.showOnceDay) {
      const dayKey = `exit-popup-shown-${this.id}`;
      localStorage.setItem(dayKey, new Date().toISOString());
    }
  }

  show() {
    if (this.isOpen || this.hasShown) return;
    if (this.shouldSkip()) return;

    this.isOpen = true;
    this.hasShown = true;
    this.markAsShown();

    // Remove inert attribute
    this.removeAttribute('inert');
    this.setAttribute('aria-hidden', 'false');

    // Show overlay
    if (this.overlay) {
      this.overlay.style.display = 'block';
    }

    // Show container with animation
    if (this.container) {
      this.container.style.display = 'block';
      this.animateIn();
    }

    // Show site-overlay if available
    const siteOverlay = document.querySelector('site-overlay');
    if (siteOverlay && siteOverlay.showOverlay) {
      siteOverlay.showOverlay();
    }

    // Lock body scroll
    document.body.classList.add('exit-popup-open');

    // Focus first input
    setTimeout(() => {
      const firstInput = this.container.querySelector('input, textarea, button');
      if (firstInput) {
        firstInput.focus();
      }
    }, 100);

    // Dispatch event
    this.dispatchEvent(new CustomEvent('exit-popup:show', { bubbles: true }));
  }

  hide() {
    if (!this.isOpen) return;

    this.isOpen = false;

    // Add inert attribute
    this.setAttribute('inert', '');
    this.setAttribute('aria-hidden', 'true');

    // Animate out
    this.animateOut();

    // Hide after animation
    setTimeout(() => {
      if (this.overlay) {
        this.overlay.style.display = 'none';
      }
      if (this.container) {
        this.container.style.display = 'none';
      }

      // Hide site-overlay if available
      const siteOverlay = document.querySelector('site-overlay');
      if (siteOverlay && siteOverlay.hideOverlay) {
        siteOverlay.hideOverlay();
      }

      // Unlock body scroll
      document.body.classList.remove('exit-popup-open');
    }, this.getAnimationDuration());

    // Dispatch event
    this.dispatchEvent(new CustomEvent('exit-popup:hide', { bubbles: true }));
  }

  animateIn() {
    if (!this.container) return;

    const animationType = this.container.dataset.animationType || 'fade';
    const duration = this.getAnimationDuration();

    this.container.style.transition = `all ${duration}ms ease-out`;

    switch (animationType) {
      case 'slide':
        this.container.style.transform = 'translateY(-20px)';
        this.container.style.opacity = '0';
        requestAnimationFrame(() => {
          this.container.style.transform = 'translateY(0)';
          this.container.style.opacity = '1';
        });
        break;
      case 'scale':
        this.container.style.transform = 'scale(0.9)';
        this.container.style.opacity = '0';
        requestAnimationFrame(() => {
          this.container.style.transform = 'scale(1)';
          this.container.style.opacity = '1';
        });
        break;
      case 'fade':
      default:
        this.container.style.opacity = '0';
        requestAnimationFrame(() => {
          this.container.style.opacity = '1';
        });
        break;
    }
  }

  animateOut() {
    if (!this.container) return;

    const animationType = this.container.dataset.animationType || 'fade';
    const duration = this.getAnimationDuration();

    this.container.style.transition = `all ${duration}ms ease-in`;

    switch (animationType) {
      case 'slide':
        this.container.style.transform = 'translateY(-20px)';
        this.container.style.opacity = '0';
        break;
      case 'scale':
        this.container.style.transform = 'scale(0.9)';
        this.container.style.opacity = '0';
        break;
      case 'fade':
      default:
        this.container.style.opacity = '0';
        break;
    }
  }

  getAnimationDuration() {
    if (!this.container) return 300;
    return parseInt(this.container.dataset.animationDuration) || 300;
  }

  async handleFormSubmit(e) {
    e.preventDefault();

    if (!this.form) return;

    // Validate form
    if (!this.validateForm()) {
      return;
    }

    // Show loading state
    if (this.submitButton) {
      const submitText = this.submitButton.querySelector('.exit-popup-submit-text');
      const loadingText = this.submitButton.querySelector('.exit-popup-submit-loading');
      
      if (submitText) submitText.style.display = 'none';
      if (loadingText) loadingText.style.display = 'inline';
      
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
    }

    // Get form data
    const formData = new FormData(this.form);
    
    try {
      const response = await fetch('/contact', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        // Show success message
        this.showSuccessMessage();
        
        // Reset form
        this.form.reset();
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your form. Please try again.');
    } finally {
      // Reset loading state
      if (this.submitButton) {
        const submitText = this.submitButton.querySelector('.exit-popup-submit-text');
        const loadingText = this.submitButton.querySelector('.exit-popup-submit-loading');
        
        if (submitText) submitText.style.display = 'inline';
        if (loadingText) loadingText.style.display = 'none';
        
        this.submitButton.disabled = false;
        this.submitButton.removeAttribute('aria-busy');
      }
    }
  }

  validateForm() {
    if (!this.form) return false;

    let isValid = true;
    const fields = this.form.querySelectorAll('input[required], textarea[required]');

    fields.forEach(field => {
      if (field.type === 'email') {
        if (!this.validateEmail(field.value)) {
          this.showFieldError(field, 'Please enter a valid email address.');
          isValid = false;
        } else {
          this.hideFieldError(field);
        }
      } else if (field.type === 'tel') {
        if (!this.validatePhone(field.value)) {
          this.showFieldError(field, 'Please enter a valid phone number.');
          isValid = false;
        } else {
          this.hideFieldError(field);
        }
      } else {
        if (!field.value.trim()) {
          this.showFieldError(field, 'This field is required.');
          isValid = false;
        } else {
          this.hideFieldError(field);
        }
      }
    });

    return isValid;
  }

  validateEmail(email) {
    if (!email) return true; // Optional fields
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  }

  validatePhone(phone) {
    if (!phone) return true; // Optional fields
    // Basic phone validation - at least 10 digits
    const phonePattern = /^[\d\s\-\+\(\)]+$/;
    const digitsOnly = phone.replace(/\D/g, '');
    return phonePattern.test(phone) && digitsOnly.length >= 10;
  }

  showFieldError(field, message) {
    field.setAttribute('aria-invalid', 'true');
    field.classList.add('exit-popup-field-error-active');

    // Find or create error element
    let errorEl = field.parentElement.querySelector('.exit-popup-field-error');
    if (!errorEl) {
      errorEl = document.createElement('small');
      errorEl.className = 'exit-popup-field-error';
      errorEl.setAttribute('role', 'alert');
      field.parentElement.appendChild(errorEl);
    }
    
    errorEl.textContent = message;
    errorEl.style.display = 'block';
  }

  hideFieldError(field) {
    field.setAttribute('aria-invalid', 'false');
    field.classList.remove('exit-popup-field-error-active');

    const errorEl = field.parentElement.querySelector('.exit-popup-field-error');
    if (errorEl) {
      errorEl.style.display = 'none';
      errorEl.textContent = '';
    }
  }

  showSuccessMessage() {
    if (!this.successMessage) return;

    // Hide form
    if (this.form) {
      this.form.style.display = 'none';
    }

    // Show success message
    this.successMessage.style.display = 'block';
    this.successMessage.setAttribute('aria-live', 'polite');

    // Auto-close after 3 seconds (optional)
    setTimeout(() => {
      this.hide();
    }, 3000);
  }

  cleanup() {
    // Remove event listeners
    // (Most are on document/window, so they'll be cleaned up automatically)
    
    // Remove history state if we added it
    if (this.historyStatePushed && window.history.state && window.history.state.exitPopup) {
      window.history.back();
    }
  }
}

// Register the custom element
if (!customElements.get('exit-popup')) {
  customElements.define('exit-popup', ExitPopup);
}

